import { useEffect, useState } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { QuestionFormData, wellnessQAService, WellnessQuestion } from "../services/WellnessQAService";
import { addDoc, collection, deleteDoc, doc, Timestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { CheckIcon, EyeIcon, MagnifyingGlassIcon, PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const WellnessQAPage: React.FC = () => {
    const [questions, setQuestions] = useState<WellnessQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [editingQuestion, setEditingQuestion] =
        useState<WellnessQuestion | null>(null);
    const [formData, setFormData] = useState<QuestionFormData>({
        question: "",
        answers: ["", "", "", "", "", "", ""],
        category: "mood",
        title: "",
        video: "",
        active: true,
    });

    const categories = [
        "mood",
        "recovery",
        "sleep",
        "stress",
        "health",
        "physical",
    ];

    const loadQuestions = async () => {
        try {
            setLoading(true);
            const fetchedQuestions = await wellnessQAService.fetchWellnessQuestions();
            setQuestions(fetchedQuestions);
        } catch (err) {
            console.error("Error fetching wellness questions:", err);
            setError("Failed to load wellness questions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadQuestions();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const questionData = {
                ...formData,
                answers: formData.answers.filter((answer) => answer.trim() !== ""),
                createdAt: editingQuestion
                ? editingQuestion.createdAt
                : Timestamp.now(),
                updatedAt: Timestamp.now(),
            };

            if (editingQuestion) {
                // Update existing question
                await updateDoc(
                doc(db, "wellnessQuestions", editingQuestion.id),
                questionData
                );
            } else {
                // Create new question
                await addDoc(collection(db, "wellnessQuestions"), questionData);
            }

            // Reset form and refresh data
            setFormData({
                question: "",
                answers: ["", "", "", "", "", "", ""],
                category: "mood",
                title: "",
                video: "",
                active: true,
            });
            setShowForm(false);
            setEditingQuestion(null);
            await loadQuestions();
        } catch (error) {
            console.error("Error saving question:", error);
        }
    };

    const handleEdit = (question: WellnessQuestion) => {
        setEditingQuestion(question);
        setFormData({
            question: question.question,
            answers: [
                ...question.answers,
                ...Array(7 - question.answers.length).fill(""),
            ],
            category: question.category,
            title: question.title || "",
            video: question.video || "",
            active: question.active,
        });
        setShowForm(true);
    };

    const handleDelete = async (questionId: string) => {
        if (window.confirm("Are you sure you want to delete this question?")) {
            try {
                await deleteDoc(doc(db, "wellnessQuestions", questionId));
                await loadQuestions();
            } catch (error) {
                console.error("Error deleting question:", error);
            }
        }
    };

    const toggleActive = async (question: WellnessQuestion) => {
        setTogglingId(question.id);
        try {
            const newActive = !question.active;
            await updateDoc(doc(db, "wellnessQuestions", question.id), {
                active: newActive,
                updatedAt: Timestamp.now(),
            });
            setQuestions(prev =>
                prev.map(q => q.id === question.id ? { ...q, active: newActive } : q)
            );
        } catch (error) {
            console.error("Error toggling question status:", error);
        } finally {
            setTogglingId(null);
        }
    };

    const handleAnswerChange = (index: number, value: string) => {
        const newAnswers = [...formData.answers];
        newAnswers[index] = value;
        setFormData({ ...formData, answers: newAnswers });
    };

    const cancelEdit = () => {
        setShowForm(false);
        setEditingQuestion(null);
        setFormData({
        question: "",
        answers: ["", "", "", "", "", "", ""],
        category: "mood",
        title: "",
        video: "",
        active: true,
        });
    };


    const filteredQuestions = questions.filter((q) => {
        const matchesCategory = selectedCategory === "all" || q.category === selectedCategory;
        const matchesSearch = searchQuery === "" || q.question.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-hidden">
                <div className="flex items-center justify-between gap-4">
                    <h1 className="text-2xl font-heading">Wellness Questions & Answers</h1>
                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search questions..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="input input-bordered input-sm !h-10 pl-9 text-sm w-56"
                            />
                        </div>
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="select select-bordered select-sm !h-10 text-sm"
                        >
                            <option value="all">All Categories</option>
                            {categories.map((category) => (
                                <option key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </option>
                            ))}
                        </select>
                        <button
                            className="btn btn-primary btn-sm !h-10 gap-2"
                            onClick={() => setShowForm(true)}
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add Question
                        </button>
                    </div>
                </div>

                {/* Questions List */}
                <div className="flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex flex-col">
                    {/* Table Header */}
                    <div className="grid grid-cols-11 gap-6 px-4 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase shrink-0 pr-8">
                        <div className="col-span-4">Question</div>
                        <div className="col-span-4">Answers</div>
                        <div className="col-span-1">Category</div>
                        <div className="col-span-1">Status</div>
                        <div className="col-span-1">Actions</div>
                    </div>

                    {/* Table Body */}
                    <div className="flex-1 overflow-y-auto min-h-0">
                      {loading ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                Loading feedback...
                            </div>
                        ) : filteredQuestions.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                No questions found
                            </div>
                        ) : (
                            filteredQuestions.map((item) => (
                                <div
                                    key={item.id}
                                    className="grid grid-cols-11 gap-6 px-4 py-3 border-b hover:bg-gray-50 items-center transition-colors"
                                >
                                    {/* Question */}
                                    <div className="col-span-4">
                                        <span className="text-sm line-clamp-1">
                                            {item.question}
                                        </span>
                                    </div>

                                    {/* Answers */}
                                    <div className="col-span-4">
                                        <span className="text-sm text-gray-600 rounded truncate block">
                                            {item.answers.join(", ")}
                                        </span>
                                    </div>

                                    {/* Category */}
                                    <div className="col-span-1 flex gap-2">
                                        <span
                                            className="inline-block px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded capitalize"
                                        >
                                            {item.category}
                                        </span>
                                    </div>

                                    {/* Status */}
                                    <div className="col-span-1">
                                        <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase rounded-full ${
                                            item.active
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-orange-100 text-orange-800'
                                        }`}>
                                            {item.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    
                                    {/* Actions */}
                                    <div className="col-span-1 flex gap-2">
                                        <button
                                            onClick={() => toggleActive(item)}
                                            disabled={togglingId === item.id}
                                            className="p-1.5 rounded bg-green-100 hover:bg-green-200 text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Toggle active status"
                                        >
                                            {togglingId === item.id ? (
                                                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                </svg>
                                            ) : (
                                                <EyeIcon className="w-4 h-4" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleEdit(item)}
                                            className="p-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
                                            title="Edit question"
                                        >
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className="p-1.5 rounded bg-red-100 hover:bg-red-200 text-red-700"
                                            title="Delete question"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Question Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50">
                    <div className="min-h-screen flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">
                                {editingQuestion ? "Edit Question" : "Add New Question"}
                            </h2>
                            <button
                                onClick={cancelEdit}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Category */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                Category
                                </label>
                                <select
                                value={formData.category}
                                onChange={(e) =>
                                    setFormData({ ...formData, category: e.target.value })
                                }
                                className="select select-bordered w-full"
                                required
                                >
                                {categories.map((category) => (
                                    <option key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                    </option>
                                ))}
                                </select>
                            </div>

                            {/* Question */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                Question
                                </label>
                                <textarea
                                value={formData.question}
                                onChange={(e) =>
                                    setFormData({ ...formData, question: e.target.value })
                                }
                                className="textarea textarea-bordered w-full"
                                rows={3}
                                required
                                />
                            </div>

                            {/* Title (Optional) */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                Title (Optional)
                                </label>
                                <input
                                type="text"
                                value={formData.title}
                                onChange={(e) =>
                                    setFormData({ ...formData, title: e.target.value })
                                }
                                className="input input-bordered w-full"
                                />
                            </div>

                            {/* Video URL (Optional) */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                Video URL (Optional)
                                </label>
                                <input
                                type="url"
                                value={formData.video}
                                onChange={(e) =>
                                    setFormData({ ...formData, video: e.target.value })
                                }
                                className="input input-bordered w-full"
                                placeholder="https://..."
                                />
                            </div>

                            {/* Answers */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                Answers (1-7 Scale)
                                </label>
                                <div className="space-y-2">
                                {formData.answers.map((answer, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                    <span className="text-sm font-medium w-8">
                                        {index + 1}:
                                    </span>
                                    <input
                                        type="text"
                                        value={answer}
                                        onChange={(e) =>
                                        handleAnswerChange(index, e.target.value)
                                        }
                                        className="input input-bordered flex-1"
                                        placeholder={`Answer for scale point ${index + 1}`}
                                        required={index < 3} // First 3 answers are required
                                    />
                                    </div>
                                ))}
                                </div>
                            </div>

                            {/* Active Status */}
                            <div className="flex items-center gap-2">
                                <input
                                type="checkbox"
                                checked={formData.active}
                                onChange={(e) =>
                                    setFormData({ ...formData, active: e.target.checked })
                                }
                                className="checkbox checkbox-primary"
                                />
                                <span className="text-sm font-medium">Active</span>
                            </div>

                            {/* Form Actions */}
                            <div className="flex justify-between gap-2 pt-4 border-t">
                                <button
                                type="button"
                                onClick={cancelEdit}
                                className=" -ghost"
                                >
                                Cancel
                                </button>
                                <button
                                type="submit"
                                className="bg-black text-white py-3 px-6 flex items-center gap-2 rounded-md"
                                >
                                <CheckIcon className="w-4 h-4" />
                                {editingQuestion ? "Update" : "Create"} Question
                                </button>
                            </div>
                            </form>
                        </div>
                        </div>
                    </div>
                    </div>
                )}
            </div>
        </DashboardWrapper>
    );
};

export default WellnessQAPage;